import { type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TopBar, Icon, useToast } from "@/components/ui";
import Button from "@/components/ui/Button";
import {
    estimateTokensForDeposit,
    formatTokenAmount,
    getCurveParams,
} from "@/utils/curveEstimator";
import { useConfig } from "@/providers";
import confettiBurst from "@/assets/shapes/confetti-burst.svg";

const SOLANA_NETWORK = import.meta.env.VITE_SOLANA_NETWORK || "devnet";

/* ─── Types ─── */
interface StakeState {
    cabal: {
        name: string;
        ticker: string;
        slug: string;
        inviteCode: string;
        depositTarget: number;
        depositCurrent: number;
    };
    deposit: number;
    stakeWeight: number;
    totalDeposited: number;
    txSignature: string;
    solscanUrl?: string;
    estimatedTokens?: number;
}

/* ════════════════════════════════════════════
   Stake Confirmed — Success Page
   ════════════════════════════════════════════ */
export default function StakeConfirmedPage(): ReactNode {
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const { INITIAL_MC_SOL, MIGRATION_MC_SOL, TOTAL_TOKEN_SUPPLY } = useConfig();
    const state = location.state as StakeState | undefined;

    if (!state) {
        return (
            <div className="relative flex min-h-screen w-full flex-col overflow-hidden max-w-[430px] mx-auto border-x border-white/5 shadow-2xl bg-[#0a0a0a]">
                <TopBar variant="minimal" showBack onBack={() => navigate("/")} />
                <div className="flex-1 flex items-center justify-center">
                    <p className="text-white/40 font-[family-name:var(--font-display)]">No deposit data found</p>
                </div>
            </div>
        );
    }

    const { cabal, deposit, totalDeposited, txSignature, solscanUrl, estimatedTokens } = state;
    const stakeWeight = cabal.depositTarget > 0 ? (deposit / cabal.depositTarget) * 100 : 0;

    const estAllocation = estimatedTokens
        ? formatTokenAmount(estimatedTokens)
        : formatTokenAmount(
            estimateTokensForDeposit(totalDeposited, getCurveParams(INITIAL_MC_SOL, MIGRATION_MC_SOL, TOTAL_TOKEN_SUPPLY).sqrtStartPrice, getCurveParams(INITIAL_MC_SOL, MIGRATION_MC_SOL, TOTAL_TOKEN_SUPPLY)).tokensOut
        );
    const thresholdReached = cabal.depositCurrent >= cabal.depositTarget;
    const inviteUrl = `${window.location.origin}/codevs/join/${cabal.inviteCode}`;

    const explorerUrl =
        solscanUrl ||
        `https://solscan.io/tx/${txSignature}${SOLANA_NETWORK === "devnet" ? "?cluster=devnet" : ""}`;

    function handleShareLink() {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isMobile && navigator.share) {
            navigator.share({
                title: `CODEV ${cabal.ticker}`,
                text: `Join me in CODEVing ${cabal.ticker}!`,
                url: inviteUrl,
            }).catch(() => { });
        } else {
            navigator.clipboard.writeText(inviteUrl).then(
                () => toast("Invite link copied to clipboard!", "success"),
                () => toast("Failed to copy link", "error"),
            );
        }
    }

    function handleShareTelegram() {
        const text = encodeURIComponent(`Join me in CODEVing ${cabal.ticker}`);
        const url = encodeURIComponent(inviteUrl);
        window.open(`https://t.me/share/url?url=${url}&text=${text}`, "_blank");
    }

    function handleShareX() {
        const text = encodeURIComponent(
            `Join me in CODEVing ${cabal.ticker} on ${inviteUrl}`
        );
        window.open(`https://x.com/intent/tweet?text=${text}`, "_blank");
    }

    const shortTx = txSignature
        ? `${txSignature.slice(0, 8)}...${txSignature.slice(-8)}`
        : "";

    return (
        <div className="relative flex h-[100dvh] w-full flex-col overflow-hidden max-w-[430px] mx-auto border-x border-white/5 shadow-2xl bg-[#0a0a0a]">
            {/* Confetti burst background */}
            <div className="absolute top-[-308px] left-[-215px] w-[831px] h-[705px] opacity-30 overflow-hidden pointer-events-none">
                <img src={confettiBurst} alt="" className="w-full h-full" />
            </div>

            <TopBar variant="minimal" title="" transparentBg showBack onBack={() => navigate(-1)} />

            <main className="relative z-10 flex-1 flex flex-col px-5 min-h-0 overflow-y-auto">
                {/* ─── Success Hero ─── */}
                <div className="flex flex-col items-center pt-[clamp(4px,1.5dvh,16px)] mb-[clamp(12px,2dvh,24px)]">
                    {/* Concentric rings + logo (single exported SVG) */}
                    <div className="relative mb-[clamp(8px,1.5dvh,16px)]">
                        {/* Warm glow behind circle */}
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[160px] rounded-full bg-[rgba(243,206,40,0.08)] blur-[60px] pointer-events-none" />
                        <Icon name="stake-logo" size={136} />

                    </div>

                    {/* Title */}
                    <div className="flex flex-col items-center gap-[6px]">
                        <h1 className="text-[clamp(26px,3.4dvh,32px)] font-bold text-white tracking-[-0.64px] leading-[1.15] font-[family-name:var(--font-display)]">
                            Deposit Confirmed!
                        </h1>
                        <p className="text-[clamp(16px,1.9dvh,18px)] font-medium text-white/50 leading-[1.15] font-[family-name:var(--font-display)] truncate">
                            You joined {cabal.ticker.toUpperCase()} launch pool
                        </p>
                    </div>
                </div>

                {/* ─── Transaction Card ─── */}
                {txSignature && (
                    <a
                        href={explorerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px] flex items-center justify-between px-4 py-3 mb-[clamp(10px,1.5dvh,18px)] group"
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            <Icon name="receipt-01" size={28} className="opacity-60 shrink-0" />
                            <div className="flex flex-col gap-[4px] min-w-0">
                                <p className="text-[14px] font-medium text-white/40 tracking-[-0.28px] leading-[1.15] font-[family-name:var(--font-display)]">
                                    Transaction
                                </p>
                                <p className="text-[16px] font-medium text-[#f3ce28] tracking-[-0.36px] leading-[1.15] font-[family-name:var(--font-display)] truncate">
                                    {shortTx}
                                </p>
                            </div>
                        </div>
                        <Icon name="external-link" size={20} className="opacity-30 group-hover:opacity-60 transition-opacity shrink-0 ml-2" />
                    </a>
                )}

                {/* ─── Stats Card (with threshold pill inside) ─── */}
                <div className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px] pt-4 px-4 pb-4 mb-[clamp(10px,1.5dvh,18px)]">
                    {/* You Deposited row */}
                    <div className="flex items-center justify-between mb-[clamp(12px,2dvh,24px)]">
                        <Icon name="donation" size={28} className="opacity-50" />
                        <div className="flex items-center gap-[6px]">
                            <p className="text-[14px] font-medium text-white/40 tracking-[-0.28px] leading-[1.15] font-[family-name:var(--font-display)]">
                                You Deposited
                            </p>
                            <p className="text-[clamp(18px,2.1dvh,20px)] font-medium text-white tracking-[-0.4px] leading-[1.15] font-[family-name:var(--font-display)]">
                                {parseFloat(parseFloat(deposit.toString()).toFixed(2))} SOL
                            </p>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="h-px w-full bg-[#252525] mb-[clamp(12px,2dvh,24px)]" />

                    {/* Pool Share + Est. Allocation */}
                    <div className="flex gap-4 mb-[clamp(12px,2dvh,24px)]">
                        <div className="flex-1 flex flex-col gap-[6px]">
                            <p className="text-[14px] font-medium text-white/40 tracking-[-0.28px] leading-[1.15] font-[family-name:var(--font-display)]">
                                Pool Share
                            </p>
                            <p className="text-[clamp(18px,2.1dvh,20px)] font-medium text-white tracking-[-0.4px] leading-[1.15] font-[family-name:var(--font-display)]">
                                {stakeWeight.toFixed(2)} %
                            </p>
                        </div>
                        <div className="flex-1 flex flex-col gap-[6px]">
                            <p className="text-[14px] font-medium text-white/40 tracking-[-0.28px] leading-[1.15] font-[family-name:var(--font-display)]">
                                Est. Allocation
                            </p>
                            <div className="flex items-end gap-[6px]">
                                <p className="text-[clamp(18px,2.1dvh,20px)] font-medium text-white tracking-[-0.4px] leading-[1.15] font-[family-name:var(--font-display)]">
                                    {estAllocation}
                                </p>
                                <p className="text-[14px] font-medium text-white tracking-[-0.28px] leading-[1.15] pb-[4px] font-[family-name:var(--font-display)] truncate">
                                    {cabal.ticker.toUpperCase()}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Threshold Status Pill — inside stats card */}
                    {thresholdReached ? (
                        <div className="backdrop-blur-[14px] bg-[rgba(40,243,104,0.05)] border-[0.5px] border-[rgba(40,243,104,0.22)] rounded-[50px] flex items-center pl-5 pr-4 py-4 gap-3">
                            <Icon name="check-circle" size={28} className="shrink-0" />
                            <p className="text-[12px] font-medium text-white leading-[1.15] font-[family-name:var(--font-display)]">
                                Deposit target reached - your token is launching soon!
                            </p>
                        </div>
                    ) : (
                        <div className="backdrop-blur-[14px] bg-[rgba(243,206,40,0.05)] border-[0.5px] border-[rgba(243,206,40,0.22)] rounded-[50px] flex items-center pl-5 pr-4 py-4 gap-3">
                            <Icon name="my-codevs" size={28} inline className="shrink-0 text-[#6E6975]" />
                            <p className="text-[12px] font-medium text-white leading-[1.15] font-[family-name:var(--font-display)]">
                                Recruit your trusted allies to hit the threshold faster.
                            </p>
                        </div>
                    )}
                </div>

                {/* ─── Share CTAs (when threshold not reached) ─── */}
                {!thresholdReached && (
                    <div className="mb-[clamp(8px,1.2dvh,16px)]">
                        <p className="text-[12px] font-medium text-white/40 text-center mb-3 font-[family-name:var(--font-display)]">
                            Recruit Allies
                        </p>
                        <div className="flex gap-3">
                            <button
                                className="flex-1 bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px] py-3.5 flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                                onClick={handleShareTelegram}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="opacity-60">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" fill="white" />
                                </svg>
                                <span className="text-[14px] font-semibold text-white font-[family-name:var(--font-display)]">
                                    Telegram
                                </span>
                            </button>
                            <button
                                className="flex-1 bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px] py-3.5 flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                                onClick={handleShareX}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="opacity-60">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="white" />
                                </svg>
                                <span className="text-[14px] font-semibold text-white font-[family-name:var(--font-display)]">
                                    X / Twitter
                                </span>
                            </button>
                        </div>

                        {/* Copy link button */}
                        <button
                            className="w-full mt-3 bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.1)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px] py-3 flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                            onClick={handleShareLink}
                        >
                            <Icon name="copy" size={16} className="opacity-50" />
                            <span className="text-[13px] font-semibold text-white/60 font-[family-name:var(--font-display)]">
                                Copy Invite Link
                            </span>
                        </button>
                    </div>
                )}

                {/* ─── Bottom CTA ─── */}
                <div className="mt-auto pt-2 pb-[clamp(8px,1.5dvh,16px)] shrink-0">
                    <Button
                        variant="cta"
                        size="lg"
                        textStyle="v2"
                        fullWidth
                        onClick={() => navigate("/codevs")}
                        className="!bg-[rgba(243,206,40,0.1)] !text-[#f3ce28] text-[clamp(18px,2.1dvh,20px)]"
                    >
                        View My Codevs
                    </Button>
                </div>
            </main>
        </div>
    );
}
